import { FC, useState, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { MenuItem } from 'Types';
import { toggleSidebar } from 'Actions';
import { clx } from 'Utils';
import { businessPartnerMenu, communityPartnerMenu } from 'Data';
import {
	useDispatch,
	useGetB2bUserProfileQuery,
	useModal,
	useDelayUnmount,
	useCommunityPartnerMe
} from 'Hooks';
import {
	Scrollbar,
	SidebarMainItem,
	ModalBoxSupport,
	FontAwesome
} from 'Elements';
import styles from 'partials/sidebar/sidebar.module.scss';

interface Props {
	isOpen?: boolean;
}

export const Sidebar: FC<Props> = ({ isOpen }) => {
	const { t } = useTranslation();
	const location = useLocation();
	const dispatch = useDispatch();
	const [openModal, toggleModal] = useModal();
	const shouldRenderModal = useDelayUnmount(openModal, 350);
	const defaultMenu: MenuItem = {
		id: 'dashboard',
		label: 'menu.dashboard',
		icon: 'gauge-low',
		to: `/`
	} as const;
	const [activeMenu, setActiveMenu] = useState<MenuItem>(defaultMenu);
	const partnerType = localStorage.getItem('partnerType');
	const { data: user, isLoading } = useGetB2bUserProfileQuery();
	// const userMenu = user?.partnerType === 2 ? businessPartnerMenu : communityPartnerMenu;

	let userMenu: any = [];
	if (partnerType === '2') {
		userMenu = businessPartnerMenu;
	}
	if (partnerType === '1') {
		userMenu = communityPartnerMenu;
	}

	const { data: agent } = useCommunityPartnerMe({
		enabled: user?.partnerType === 1
	});
	const IsPremium = agent?.commissionGroupInfo?.isPremium ?? false;

	const handleActiveMenu = (menu: MenuItem) => {
		setActiveMenu(activeMenu === menu ? defaultMenu : menu);
	};

	useLayoutEffect(() => {
		dispatch(toggleSidebar(false));
	}, [location.pathname, dispatch]);

	return (
		<aside
			className={clx(
				styles.sidebar,
				isOpen && styles.open,
				!isOpen && styles.close
			)}>
			<Scrollbar className={styles.scrollbar}>
				{isLoading && (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: '16px',
							padding: '16px'
						}}>
						{Array.from({ length: 5 }).map((_, idx) => (
							<div
								key={idx}
								style={{
									height: '32px',
									borderRadius: '6px',
									backgroundColor: 'rgba(255, 255, 255, 0.1)',
									animation:
										'sidebar-skeleton-loading 1.2s ease-in-out infinite'
								}}
							/>
						))}
						<style>
							{`
								@keyframes sidebar-skeleton-loading {
								    0% { opacity: 1; }
		                            50% { opacity: 0.4; }
		                            100% { opacity: 1; }
								}
								div[style*="sidebar-skeleton-loading"] {
									background-size: 200px 100%;
								}
							`}
						</style>
					</div>
				)}
				{!isLoading && userMenu && (
					<div className="d-flex flex-column h-100 justify-content-between">
						<ul className={styles.list}>
							{userMenu.map((menu) => {
								return (
									<SidebarMainItem
										key={menu.id}
										menu={menu}
										index={menu.id}
										IsPremium={IsPremium}
										activeMenu={activeMenu as MenuItem}
										onClick={handleActiveMenu}
									/>
								);
							})}
						</ul>
					</div>
				)}
			</Scrollbar>
			<div
				role="button"
				className={clx(
					styles.support,
					'bg-primary px-3 d-flex align-items-center gap-3'
				)}
				onClick={toggleModal}>
				<FontAwesome icon="user-headset" size="xl" />
				{t('menu.contact-support')}
			</div>
			{shouldRenderModal && (
				<ModalBoxSupport isOpen={openModal} onClose={toggleModal} />
			)}
		</aside>
	);
};
